import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { getAssemblyLine } from '../lib/api';
import { parseAssemblyLineJson, toAssemblyLineExport, downloadJson, assemblyLineFileName } from '../lib/assemblyLineIo';
import { I } from '../components/Shared';
import { Shell } from '../components/Shell';

export function AssemblyLines() {
  const navigate = useNavigate();
  const assemblyLines = useStore((s) => s.assemblyLines);
  const loadAssemblyLines = useStore((s) => s.loadAssemblyLines);
  const loadAssemblyLineForEdit = useStore((s) => s.loadAssemblyLineForEdit);
  const deleteAssemblyLineFromServer = useStore((s) => s.deleteAssemblyLineFromServer);
  const clearEditingAssemblyLine = useStore((s) => s.clearEditingAssemblyLine);
  const setImportedDraft = useStore((s) => s.setImportedDraft);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void loadAssemblyLines();
  }, [loadAssemblyLines]);

  const handleDelete = async (id: string, name: string): Promise<void> => {
    if (!window.confirm(`Delete assembly line "${name}"?`)) return;
    await deleteAssemblyLineFromServer(id);
  };

  const handleNew = (): void => {
    clearEditingAssemblyLine();
    navigate('/assembly-lines/new');
  };

  const handleImportClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    try {
      const draft = parseAssemblyLineJson(await file.text());
      setImportedDraft(draft);
      navigate('/assembly-lines/new');
    } catch (err: any) {
      window.alert(`Could not import assembly line: ${err?.message ?? 'unknown error'}`);
    }
  };

  const handleExport = async (id: string): Promise<void> => {
    try {
      const full = await getAssemblyLine(id);
      downloadJson(assemblyLineFileName(full.name), toAssemblyLineExport(full));
    } catch (err: any) {
      window.alert(`Could not export assembly line: ${err?.message ?? 'unknown error'}`);
    }
  };

  return (
    <Shell active="assemblyLines">

      <main style={{ padding: '32px 28px 80px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 22 }}>
          <div>
            <div className="t-micro" style={{ marginBottom: 6 }}>FACTORY · ASSEMBLY LINES</div>
            <h1 className="t-h1" style={{ margin: 0 }}>Assembly Lines</h1>
            <p className="muted" style={{ margin: '4px 0 0' }}>Reusable pipeline templates. Each defines which phases and skills run for an app.</p>
          </div>
          <span style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--sm" onClick={() => void loadAssemblyLines()}>{I.refresh} Refresh</button>
            <button className="btn btn--sm" onClick={handleImportClick}>Import Assembly Line</button>
            <button className="btn btn--accent btn--sm" onClick={handleNew}>{I.plus} New Assembly Line</button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={(e) => void handleImportFile(e)}
            />
          </div>
        </div>

        {/* table */}
        {assemblyLines.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <span className="muted">No assembly lines yet. </span>
            <button className="btn btn--ghost btn--sm" onClick={handleNew}>Create one</button>
          </div>
        ) : (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2.5fr 1.8fr 64px 64px 64px 170px',
                padding: '12px 18px',
                background: 'var(--surface-2)',
                borderBottom: '1px solid var(--line)',
                alignItems: 'center',
                gap: 10,
              }}
            >
              {["Assembly Line", "Description", 'Phases', 'Steps', 'Version', ''].map((h) => (
                <span key={h} className="t-micro">{h}</span>
              ))}
            </div>
            {assemblyLines.map((b, i) => (
              <div
                key={b.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2.5fr 1.8fr 64px 64px 64px 170px',
                  padding: '16px 18px',
                  borderBottom: i < assemblyLines.length - 1 ? '1px solid var(--line)' : 'none',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  {(() => {
                    const chipLabel = b.slug?.toUpperCase() || b.name.slice(0, 4).toUpperCase();
                    return (
                      <span
                        className="chip"
                        title={chipLabel}
                        style={{ minWidth: 0, maxWidth: '55%', flexShrink: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', lineHeight: '20px' }}
                      >
                        {chipLabel}
                      </span>
                    );
                  })()}
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); void loadAssemblyLineForEdit(b.id).then(() => navigate(`/assembly-lines/${b.id}/edit`)); }}
                    style={{ color: 'var(--ink)', fontWeight: 600, textDecoration: 'none', minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {b.name}
                  </a>
                </div>
                <span className="t-small muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.description}</span>
                <span className="t-num">{b.phaseCount}</span>
                <span className="t-num">{b.stepCount}</span>
                <span className="t-num">v{b.version}</span>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                  <button className="btn btn--ghost btn--sm" onClick={() => void loadAssemblyLineForEdit(b.id).then(() => navigate(`/assembly-lines/${b.id}/edit`))}>Edit</button>
                  <button className="btn btn--ghost btn--sm" onClick={() => void handleExport(b.id)} title="Download as JSON">Export</button>
                  <button className="btn btn--ghost btn--sm btn--icon" style={{ color: 'var(--err)' }} onClick={() => void handleDelete(b.id, b.name)}>{I.trash}</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="perf-edge" style={{ marginTop: 28 }} />
        <div style={{ display: 'flex', gap: 18, marginTop: 14, alignItems: 'center' }}>
          <span className="t-micro muted">{assemblyLines.length} ASSEMBLY LINES</span>
        </div>
      </main>
    </Shell>
  );
}
